/**
 * `<FileDropOverlay>` — the dashed-border "Drop files to attach" panel
 * shown while an OS-file drag is over a drop surface. Presentational
 * only; pair it with `useOsFileDrop` (which owns the drag state) on a
 * `relative`-positioned wrapper.
 *
 * Generalized from `ChatDropZone`'s inline overlay. `pointer-events-none`
 * so the drop lands on the surface behind it; `drop-hint-enter` plays the
 * shared fade+rise (reduced-motion aware, defined in `global.css`).
 * `aria-live="polite"` announces the drop affordance to screen readers.
 *
 * Copy is the caller's — pass a localized `label`. The overlay itself
 * holds no strings.
 */

import { Upload } from 'lucide-react'

interface FileDropOverlayProps {
  /** Localized affordance text, e.g. "Drop files to attach to this task". */
  label: string
  /**
   * Optional secondary line under the label — e.g. "Images are added to
   * the description". Rendered muted + smaller. Chat leaves it unset.
   */
  hint?: string
  /**
   * Make the overlay the drag target (`pointer-events-auto`) instead of
   * passing events through to the surface below. Hardening for surfaces
   * whose underlying DOM would otherwise react to the drag (e.g. a
   * ProseMirror editor painting its dropcursor) — the drag events still
   * bubble to the wrapper's handlers, so the drop routing is unchanged.
   * Default stays pass-through (chat / card / comment).
   */
  interceptDrag?: boolean
  /**
   * Cover the surface edge to edge instead of the default 8px inset. For a
   * section whose own content runs to its edges (the card's Attachments
   * grid and empty-state box), where an inset frame looks narrower than
   * the thing it labels.
   */
  flush?: boolean
}

export function FileDropOverlay({
  label,
  hint,
  interceptDrag = false,
  flush = false,
}: FileDropOverlayProps) {
  return (
    <output
      // text-center keeps the label + hint readable when a narrow column
      // (e.g. the wiki doc pane at 1024px with both rails open) wraps them —
      // ragged-left wrap touches the dashed border and reads broken.
      className={`drop-hint-enter ${interceptDrag ? 'pointer-events-auto' : 'pointer-events-none'} absolute ${flush ? 'inset-0' : 'inset-2'} z-30 flex flex-col items-center justify-center gap-1 rounded-lg border-2 border-dashed border-accent bg-bg-elevated/85 px-4 py-3 text-center text-sm font-medium text-fg shadow-sm`}
      aria-live="polite"
    >
      <Upload aria-hidden="true" className="mb-1 h-6 w-6 text-accent" />
      {label}
      {hint && <span className="text-xs font-normal text-fg-muted">{hint}</span>}
    </output>
  )
}
